import * as React from 'react';
import { type LucideIcon } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

export type EmptyStateVariant = 'card' | 'dashed' | 'bare';

export interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description?: string;
  action?: React.ReactNode;
  variant?: EmptyStateVariant;
  className?: string;
}

const VARIANTS: Record<EmptyStateVariant, string> = {
  card: '',
  dashed: 'border-dashed bg-transparent shadow-none',
  bare: 'border-0 bg-transparent shadow-none',
};

const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  ({ icon: Icon, title, description, action, variant = 'card', className }, ref) => (
    <Card
      ref={ref}
      data-slot="empty-state"
      className={cn(
        'flex flex-col items-center justify-center gap-3 px-6 py-16 text-center',
        VARIANTS[variant],
        className,
      )}
    >
      <span className="grid size-12 place-items-center rounded-xl bg-muted text-muted-foreground">
        {/* mx-auto is load-bearing: .revy-ui makes every svg display:block, so the
            wrapper's text-align no longer centres the glyph. */}
        <Icon className="mx-auto size-6" strokeWidth={1.6} />
      </span>
      <div className="max-w-prose">
        <p className="revy-cjk text-sm font-medium leading-[1.5]">{title}</p>
        {description && (
          <p className="revy-cjk mt-1 text-xs leading-[1.5] text-muted-foreground">{description}</p>
        )}
      </div>
      {action}
    </Card>
  ),
);
EmptyState.displayName = 'EmptyState';

export { EmptyState };
export default EmptyState;
